'use client'

import { useEffect, useMemo, useRef } from 'react'
import { useLenis } from 'lenis/react'
import LINKS from 'src/_helpers/links'
import { AuthorListItem } from 'src/components/authors/list/AuthorListItem'
import { ProductTile } from 'src/components/design-system'
import Typography from 'src/components/design-system/Typography'
import { Pagination, SCROLL_OFFSET } from 'src/components/list/Pagination'
import ListSortControls from 'src/components/list/ListSortControls'
import { getTileBorderClasses, getTileGridClass } from 'src/components/list/getTileBorderClasses'
import { meiliHitToProductTile } from 'src/components/list/meiliHitToProductTile'
import { getMediaUrl } from 'src/_helpers/client/getMediaUrl'
import { useCurrentLocale } from 'src/i18n/client/useCurrentLocale'
import { useTranslateEntry } from 'src/i18n/client/useTranslateEntry'
import useJournalDomainRoutes from 'src/components/journals/domain/useJournalDomainRoutes'
import { formatResultsFound } from 'src/_helpers/plural'

const safeString = (value) => (typeof value === 'string' ? value.trim() : '')

// titleHtml niesie kursywę i indeksy tytułu; title w indeksie to zwykły tekst do wyszukiwania.
const getHitTitle = (hit, locale) =>
  [
    hit?.titleHtml,
    hit?.title,
    hit?.name,
    hit?.translations?.[locale]?.titleHtml,
    hit?.translations?.[locale]?.title,
    hit?.translations?.pl?.titleHtml,
    hit?.translations?.pl?.title,
    hit?.translations?.en?.titleHtml,
    hit?.translations?.en?.title,
  ]
    .map(safeString)
    .find(Boolean)

const buildAuthorImageSrc = (hit) => {
  const thumbPath = safeString(hit?.thumbs?.list?.url)
  const thumbVersion = hit?.thumbs?.list?.version

  if (!thumbPath.startsWith('/')) {
    return null
  }

  return getMediaUrl(thumbPath, typeof thumbVersion === 'number' ? thumbVersion : 1)
}

const TYPE_LABELS = {
  journal: 'T_SEARCH_RESULT_TYPE_JOURNAL',
  journal_article: 'T_SEARCH_RESULT_TYPE_JOURNAL_ARTICLE',
  journal_issue: 'T_SEARCH_RESULT_TYPE_JOURNAL_ISSUE',
  journal_page: 'T_SEARCH_RESULT_TYPE_JOURNAL_PAGE',
  monograph: 'T_SEARCH_RESULT_TYPE_MONOGRAPH',
  monographs: 'T_SEARCH_RESULT_TYPE_MONOGRAPH',
  monograph_chapter: 'T_SEARCH_RESULT_TYPE_MONOGRAPH_CHAPTER',
  author: 'T_SEARCH_RESULT_TYPE_AUTHOR',
}
const HIDDEN_TYPE_TAGS = new Set(['monograph', 'monographs'])

const getTypeLabel = (type, translateEntry) =>
  TYPE_LABELS[type] ? translateEntry(TYPE_LABELS[type]) : translateEntry('T_SEARCH_RESULT_TYPE_GENERIC')

const SearchResults = ({
  results,
  isLoading,
  page,
  pages,
  onPageChange,
  routeContext = 'main',
  onResultClick,
  autoScroll = false,
  withSidebar = false,
  showOpenAccessTag = false,
  showPreviewTag = false,
  sortOptions = [],
  sortValue = 'relevance',
  onSortValueChange,
}) => {
  const locale = useCurrentLocale()
  const translateEntry = useTranslateEntry()
  const journalRoutes = useJournalDomainRoutes()
  const lenis = useLenis()
  const resultsTopRef = useRef(null)
  const listTopRef = useRef(null)

  const desktopColumns = withSidebar ? 4 : 5

  const items = useMemo(() => {
    const hits = Array.isArray(results?.hits) ? results.hits : []

    return hits.map((hit) => {
      const type = safeString(hit?.type)

      if (type === 'author') {
        return { kind: 'author', hit }
      }

      const tile = meiliHitToProductTile(hit, locale, {
        includeTypeTag: true,
        includeOpenAccessTag: showOpenAccessTag,
        includePreviewTag: showPreviewTag,
        routeContext,
        journalRoutes,
      })
      if (tile) {
        return { kind: 'tile', tile }
      }

      return { kind: 'fallback', hit }
    })
  }, [results?.hits, locale, routeContext, journalRoutes, showOpenAccessTag, showPreviewTag])

  useEffect(() => {
    if (!autoScroll || isLoading || !results) return

    const animationFrame = window.requestAnimationFrame(() => {
      const target = listTopRef.current || resultsTopRef.current
      if (!target) return

      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches

      if (lenis) {
        lenis.scrollTo(target, { offset: SCROLL_OFFSET, immediate: reduceMotion })
        return
      }

      window.scrollTo({
        top: target.getBoundingClientRect().top + window.scrollY + SCROLL_OFFSET,
        behavior: reduceMotion ? 'auto' : 'smooth',
      })
    })

    return () => window.cancelAnimationFrame(animationFrame)
  }, [autoScroll, isLoading, lenis, results])

  if (isLoading) {
    return (
      <Typography variant="body3-normal" className="mt-25 mb-32 text-center" color="text-basic-gray2">
        {translateEntry('T_LOADING')}
      </Typography>
    )
  }

  if (!results) {
    return null
  }

  const hasHits = items.length > 0
  const currentPage = page ?? results.page
  const totalPages = pages ?? results.totalPages
  const totalHits = results.totalHits ?? 0
  const hasSortOptions = Array.isArray(sortOptions) && sortOptions.length > 0

  return (
    <div ref={resultsTopRef} className="d:px-24 xl:px-16">
      {hasSortOptions ? (
        <ListSortControls
          page={currentPage}
          totalPages={totalPages}
          onPageChange={onPageChange}
          sortOptions={sortOptions}
          sortValue={sortValue}
          onSortValueChange={onSortValueChange}
          leading={
            <Typography variant="body3-normal" color="text-basic-gray2">
              {formatResultsFound(totalHits, locale, translateEntry)}
            </Typography>
          }
        />
      ) : (
        <div className="mb-32 flex items-center justify-between gap-24">
          <Typography variant="body3-normal" color="text-basic-gray2">
            {formatResultsFound(totalHits, locale, translateEntry)}
          </Typography>
          {hasHits && (
            <Pagination
              page={currentPage}
              pages={totalPages}
              setPage={onPageChange}
              scrollTarget={autoScroll ? null : undefined}
            />
          )}
        </div>
      )}
      {hasHits && (
        <div
          ref={listTopRef}
          className={`list-elements mb-32 grid ${getTileGridClass(desktopColumns, { withSidebar })}`}
        >
          {items.map((item, index) => {
            const bordersClassName = getTileBorderClasses(index, { desktopColumns, withSidebar })
            const itemWrapperClassName = [bordersClassName, 'mt-25 pb-25'].filter(Boolean).join(' ')

            if (item.kind === 'tile') {
              const tile = item.tile
              const key = tile?.id ?? `tile-${index}`

              return (
                <div key={key} className={itemWrapperClassName}>
                  <ProductTile
                    imageSrc={tile?.imageSrc}
                    imageAlt={tile?.imageAlt}
                    tags={tile?.tags
                      ?.filter((tag) => !HIDDEN_TYPE_TAGS.has(tag.label))
                      .map((tag) => ({
                        ...tag,
                        label: TYPE_LABELS[tag.label] ? translateEntry(TYPE_LABELS[tag.label]) : tag.label,
                      }))}
                    title={tile?.title}
                    subtitle={tile?.subtitle}
                    footerLines={tile?.footerLines}
                    metadata={tile?.metadata}
                    ctaHref={tile?.ctaHref ?? null}
                    ctaLabel={tile?.ctaHref ? translateEntry('T_SEE_MORE') : null}
                    isArticle={tile?.isArticle}
                    onClick={onResultClick}
                  />
                </div>
              )
            }

            if (item.kind === 'author') {
              const hit = item.hit
              const slug = safeString(hit?.slug)
              const detailUrl =
                slug && LINKS.AUTHORS?.DETAIL?.[locale] ? LINKS.AUTHORS.DETAIL[locale].replace(':slug', slug) : null
              const orcid = safeString(hit?.orcid) || safeString(hit?.orcids?.[0])
              const address = safeString(hit?.country) || safeString(hit?.countries?.[0])
              const affiliations = hit?.affiliations?.length ? hit.affiliations : []
              const email = safeString(hit?.email)
              const position = safeString(hit?.position)

              return (
                <div key={hit?.id ?? `author-${index}`} className={itemWrapperClassName}>
                  <AuthorListItem
                    position={position}
                    name={hit?.fullName || hit?.title}
                    orcid={orcid}
                    affiliations={affiliations}
                    address={address}
                    email={email}
                    imageSrc={buildAuthorImageSrc(hit)}
                    detailUrl={detailUrl}
                    locale={locale}
                    className="gap-24"
                    onClick={onResultClick}
                    showImagePlaceholder
                    tags={[{ label: getTypeLabel('author', translateEntry) }]}
                  />
                </div>
              )
            }

            const hit = item.hit
            const type = safeString(hit?.type)
            const title = getHitTitle(hit, locale) || translateEntry('T_UNTITLED')

            return (
              <div key={hit?.id ?? `fallback-${index}`} className={itemWrapperClassName}>
                <ProductTile
                  title={title}
                  subtitle={safeString(hit?.subtitle) || safeString(hit?.translations?.[locale]?.subtitle)}
                  tags={type ? [{ label: getTypeLabel(type, translateEntry) }] : []}
                  ctaHref={null}
                  ctaLabel={null}
                  isArticle={type === 'journal_article' || type === 'monograph_chapter'}
                  onClick={onResultClick}
                />
              </div>
            )
          })}
        </div>
      )}

      {hasHits && (
        <Pagination
          className="justify-center"
          page={currentPage}
          pages={totalPages}
          setPage={onPageChange}
          scrollTarget={autoScroll ? null : undefined}
        />
      )}
    </div>
  )
}

export default SearchResults
